import { EmployeeAvatar } from '@/components/ui'
import type { DispoCalendarEmployee, DispoCalendarEvent } from '@shared/types'
import { startOfDay } from 'date-fns'
import {
  CALENDAR_DAY_START_HOUR,
  CALENDAR_DAY_END_HOUR,
  CALENDAR_HOURS_IN_RANGE,
  CALENDAR_DAY_DURATION_MINUTES,
} from './calendar-config.ts'
import { DispositionCalendarEventBar } from './DispositionCalendarEventBar.tsx'
import { DispositionCalendarOutOfRangeIndicator } from './DispositionCalendarOutOfRangeIndicator.tsx'

const ABSENCE_COLOR_MAP: Record<string, string> = {
  VACATION: '#22c55e',
  ILLNESS: '#ef4444',
  ABSENCE: '#f97316',
  STAND_BY: '#eab308',
}

function absenceGradient(types: string[]): string {
  const colors = [...new Set(types)].map((t) => ABSENCE_COLOR_MAP[t] ?? '#9ca3af')
  if (colors.length === 1) return colors[0]!
  const step = 100 / colors.length
  const stops = colors.flatMap((c, i) => [`${c} ${i * step}%`, `${c} ${(i + 1) * step}%`])
  return `linear-gradient(90deg, ${stops.join(', ')})`
}

function getLocalDateKey(tsSeconds: number): string {
  const d = new Date(tsSeconds * 1000)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

interface PlacedEvent {
  event: DispoCalendarEvent
  left: number
  width: number
  stackRow: number
  totalRows: number
  clippedLeft: boolean
  clippedRight: boolean
}

interface DayEventGroups {
  placed: PlacedEvent[]
  beforeEvents: DispoCalendarEvent[]
  afterEvents: DispoCalendarEvent[]
}

function classifyEventsForDay(events: DispoCalendarEvent[], dayStart: Date): DayEventGroups {
  const dayMidnightSec = Math.floor(dayStart.getTime() / 1000)
  const visibleStartSec = dayMidnightSec + CALENDAR_DAY_START_HOUR * 3600
  const visibleEndSec = dayMidnightSec + CALENDAR_DAY_END_HOUR * 3600
  const durationSec = CALENDAR_DAY_DURATION_MINUTES * 60

  const beforeEvents: DispoCalendarEvent[] = []
  const afterEvents: DispoCalendarEvent[] = []
  const rawPlaced: Array<Omit<PlacedEvent, 'totalRows'>> = []
  const rowEnds: number[] = []

  for (const event of events) {
    const rawStart = event.startTs
    const rawEnd = event.endTs != null
      ? event.endTs
      : event.startTs + (event.durationMinutes ?? 30) * 60

    // Completely before the visible window
    if (rawEnd <= visibleStartSec) {
      beforeEvents.push(event)
      continue
    }
    // Completely after the visible window
    if (rawStart >= visibleEndSec) {
      afterEvents.push(event)
      continue
    }

    const clippedLeft = rawStart < visibleStartSec
    const clippedRight = rawEnd > visibleEndSec
    const evStart = Math.max(rawStart, visibleStartSec)
    const evEnd = Math.min(rawEnd, visibleEndSec)

    const left = ((evStart - visibleStartSec) / durationSec) * 100
    const right = ((evEnd - visibleStartSec) / durationSec) * 100
    const width = right - left

    // Find first non-overlapping stack row
    let row = 0
    let placedInRow = false
    for (let r = 0; r < rowEnds.length; r++) {
      if ((rowEnds[r] ?? 0) <= evStart) {
        row = r
        rowEnds[r] = evEnd
        placedInRow = true
        break
      }
    }
    if (!placedInRow) {
      row = rowEnds.length
      rowEnds.push(evEnd)
    }

    rawPlaced.push({ event, left, width, stackRow: row, clippedLeft, clippedRight })
  }

  const totalRows = rowEnds.length || 1
  return {
    placed: rawPlaced.map((p) => ({ ...p, totalRows })),
    beforeEvents,
    afterEvents,
  }
}

interface DayColumn {
  date: Date
  key: string
}

interface DispositionCalendarEmployeeRowProps {
  employee: DispoCalendarEmployee
  days: DayColumn[]
  showAvatar: boolean
  onEventClick?: (event: DispoCalendarEvent) => void
}

/** Hour grid lines: one vertical line per full hour inside the visible window (excluding boundaries). */
function HourGridLines() {
  // Lines at 1, 2, … HOURS_IN_RANGE-1 (i.e. 09:00 … 17:00 for an 08–18 window)
  return (
    <>
      {Array.from({ length: CALENDAR_HOURS_IN_RANGE - 1 }, (_, i) => {
        const pct = ((i + 1) / CALENDAR_HOURS_IN_RANGE) * 100
        return (
          <div
            key={i}
            className="absolute top-0 bottom-0 w-px bg-gray-100 pointer-events-none"
            style={{ left: `${pct}%` }}
          />
        )
      })}
    </>
  )
}

export function DispositionCalendarEmployeeRow({
  employee,
  days,
  showAvatar,
  onEventClick,
}: DispositionCalendarEmployeeRowProps) {
  const eventsByDay = new Map<string, { timed: DispoCalendarEvent[]; absences: DispoCalendarEvent[] }>()
  for (const day of days) {
    eventsByDay.set(day.key, { timed: [], absences: [] })
  }

  for (const event of employee.events) {
    const key = getLocalDateKey(event.startTs)
    const slot = eventsByDay.get(key)
    if (!slot) continue
    if (event.isAbsence || event.isAllDay) {
      slot.absences.push(event)
    } else {
      slot.timed.push(event)
    }
  }

  return (
    <div className="grid grid-cols-[160px_1fr] border-b border-gray-100 last:border-b-0">
      {/* Employee label */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-r border-gray-100">
        {showAvatar && (
          <EmployeeAvatar
            name={employee.name}
            initials={employee.initials || undefined}
            size="xs"
          />
        )}
        <span className="text-xs font-medium text-gray-700 truncate">{employee.name}</span>
      </div>

      {/* Day columns */}
      <div className="grid" style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}>
        {days.map((day) => {
          const slot = eventsByDay.get(day.key)!
          const { placed, beforeEvents, afterEvents } = classifyEventsForDay(
            slot.timed,
            startOfDay(day.date),
          )
          const hasAbsence = slot.absences.length > 0

          return (
            <div
              key={day.key}
              className="flex border-r border-gray-100 last:border-r-0"
              style={{ height: '48px' }}
            >
              {/* Left wing: Vor-Schicht indicator (also serves as day separator) */}
              <div className="w-4 shrink-0 bg-gray-50 border-r border-gray-200">
                {beforeEvents.length > 0 && (
                  <DispositionCalendarOutOfRangeIndicator
                    events={beforeEvents}
                    side="left"
                    onEventClick={onEventClick}
                  />
                )}
              </div>

              {/* Main event area */}
              <div className="flex-1 relative">
                {/* Absence indicator strip */}
                {hasAbsence && (
                  <div
                    className="absolute top-0 left-0 right-0 h-1.5 opacity-80 pointer-events-none"
                    style={{ background: absenceGradient(slot.absences.map((a) => a.type)) }}
                    title={slot.absences.map((a) => a.type).join(', ')}
                  />
                )}

                {/* Hour grid lines */}
                <HourGridLines />

                {/* Event bars */}
                {placed.map((p) => (
                  <DispositionCalendarEventBar
                    key={p.event.id}
                    event={p.event}
                    left={p.left}
                    width={p.width}
                    stackRow={p.stackRow}
                    totalRows={p.totalRows}
                    clippedLeft={p.clippedLeft}
                    clippedRight={p.clippedRight}
                    onClick={onEventClick}
                  />
                ))}
              </div>

              {/* Right wing: Nach-Schicht indicator (also serves as day separator) */}
              <div className="w-4 shrink-0 bg-gray-50 border-l border-gray-200">
                {afterEvents.length > 0 && (
                  <DispositionCalendarOutOfRangeIndicator
                    events={afterEvents}
                    side="right"
                    onEventClick={onEventClick}
                  />
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
